import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { UUID } from "crypto";
import { Box, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { Check } from "@mui/icons-material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { signIn } from "aws-amplify/auth";
import { FieldWrapper, StepActions, PageHeader, SectionHeader, CountrySelect, ChoiceToggle } from "@components/ui_v2";
import { RootState, useAppDispatch } from "@store/store";
import {
  setAccountUserPhone,
  setLoanId,
  setClientDetailsId,
  setId_application,
  set_Id_applicationCondition,
  setPersonalData,
  setCompanyData,
  setLegalRepresentative,
  setLoanStatus,
} from "@store/slices/registerSlice";
import { setModalErrorVisible } from "@store/slices/appSlice";
import {
  useRegisterUserMutation,
  useLazyGetUserQuery,
  usePatchUserMutation,
  useRequestPhoneCodeMutation,
} from "@api/onboarding";
import { useLazyGetSessionInfoQuery } from "@api/session";
import { useLazyGetPersonTypeQuery } from "@api/catalogs";
import { usePostPersonMutation, usePatchPersonMutation } from "@api/personasApi";
import { useLazyGetCuentasByIdQuery, usePatchCuentaMutation } from "@api/account";
import {
  PayloadRegisterUser,
  PayloadPersonPatch,
  PayloadRequestPhoneCode,
  PayloadAccountPatch,
  PayloadPatchUser,
} from "@interfaces/request";
import { CatalogPersonType } from "@interfaces/catalogs";
import { ApiError, TaxSystemConfigResponse } from "@interfaces/response";
import { SiNo } from "@interfaces/informacionEmpresa";
import {
  PATHS_COTIZADORES_V2,
  PATHS_TOFU_V2,
  PERSON_CATALOG,
  STATUS_CATALOG,
  TAX_SYSTEM_TYPE,
} from "@helpers/constantes";
import { transformTitleCase } from "@helpers/funciones";
import {
  fromBusinessGroupResponse,
  fromPfaeBusinessResponse,
  toBusinessGroupPayload,
  toPfaeBusinessPayload,
} from "@helpers/informacionEmpresaHelpers";
import { NAME, LAST_NAMES, PHONE_NUMBER, RFC_PFAE, RFC_PM } from "@utils/expreciones";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";

type FormValues = {
  // Empresa / Tu negocio
  rfc: string;
  company_name: string;
  // Grupo empresarial (PM): `business_group` + `business_group_detail`
  enGrupoEmpresarial: SiNo;
  numEmpresasGrupo: string;
  esMatrizGrupo: SiNo;
  rfcMatrizGrupo: string;
  // Tu negocio (PFAE): van en `business_group_detail`
  years_of_operation: string;
  number_of_employees: string;
  // Solicitante
  name: string;
  first_lastname: string;
  second_lastname: string;
  country_code: string;
  phone: string;
  person_type_id: string;
  rol_detail: string;
};

const SI_NO_OPTIONS = [
  { value: "Sí", label: "Sí" },
  { value: "No", label: "No" },
];

const EMPTY_ID = "00000000-0000-0000-0000-000000000000";

/** Entero mayor o igual a cero, escrito como texto (`"6"`). */
const isNonNegativeInteger = (value?: string) => !value || /^\d+$/.test(value.trim());

export const RegistroDatosPersonales = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * `crear_cuenta` necesita los query params de la cotización; navegar a su ruta "pelona" lo hacía caer
   * en el login. Se regresa por historial (vuelve con sus params) y, si no hay historial (recarga o
   * enlace directo, `location.key === "default"`), al cotizador del tenant.
   */
  const handleBack = () => {
    if (location.key !== "default") {
      navigate(-1);
      return;
    }
    navigate(PATHS_COTIZADORES_V2.credito_simple.path);
  };
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);
  const clientDetails = useSelector((state: RootState) => state.register.personal_data);
  const companyData = useSelector((state: RootState) => state.register.company);

  // El régimen viene del cotizador; el RFC se valida contra él, como en el legacy (`tofu/pm`).
  const isPFAERequest = application.tax_system_type === TAX_SYSTEM_TYPE.PFAE.code;
  const isPMRequest = application.tax_system_type === TAX_SYSTEM_TYPE.PM.code;
  const RFC_REGEX = isPMRequest ? RFC_PM : RFC_PFAE;
  const RFC_MAX_LENGTH = isPMRequest ? 12 : 13;
  // Con la CIEC ya validada el RFC quedó fijado por el SAT: no se edita ni se vuelve a mandar.
  const rfcBloqueado = companyData.is_correct_ciec;

  const [registerUser] = useRegisterUserMutation();
  const [patchAccount] = usePatchCuentaMutation();
  const [getAccount] = useLazyGetCuentasByIdQuery();
  const [patchUser] = usePatchUserMutation();
  const [requestPhoneCode] = useRequestPhoneCodeMutation();
  const [getPersonTypesTrigger] = useLazyGetPersonTypeQuery();
  const [getSessionInfo] = useLazyGetSessionInfoQuery();
  const [getUserData] = useLazyGetUserQuery();
  const [saveLegalRepresentative] = usePostPersonMutation();
  const [patchPerson] = usePatchPersonMutation();
  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();

  const [isLoadingInitial, setIsLoadingInitial] = useState(false);
  const [personTypeCatalog, setPersonTypeCatalog] = useState<CatalogPersonType[]>([]);
  const [otherPersonTypeId, setOtherPersonTypeId] = useState<string | null>(null);
  const [personTypeIdsForRep, setPersonTypeIdsForRep] = useState<string[]>([]);
  const [personId, setPersonId] = useState("");
  const [taxSystems, setTaxSystems] = useState<TaxSystemConfigResponse | null>(null);

  const validationSchema = useMemo(
    () =>
      yup.object().shape({
        // Empresa: mismas reglas que el legacy (`tofu/pm/RegistroDatosPersonales`).
        rfc: yup.string().required("Campo requerido").matches(RFC_REGEX, "Formato incorrecto, inténtalo de nuevo"),
        company_name: yup.string().required("Campo requerido"),
        // Grupo empresarial: solo PM (reglas que tenía `InformacionEmpresa`).
        enGrupoEmpresarial: isPMRequest ? yup.string().required("Campo requerido") : yup.string(),
        numEmpresasGrupo: yup.string().when("enGrupoEmpresarial", {
          is: "Sí",
          then: (schema) =>
            schema
              .required("Campo requerido")
              .test("min_empresas", "Un grupo tiene al menos 2 empresas", (value) => Number(value) >= 2),
        }),
        esMatrizGrupo: yup.string().when("enGrupoEmpresarial", {
          is: "Sí",
          then: (schema) => schema.required("Campo requerido"),
        }),
        rfcMatrizGrupo: yup.string().when(["enGrupoEmpresarial", "esMatrizGrupo"], {
          is: (enGrupo: string, esMatriz: string) => enGrupo === "Sí" && esMatriz === "No",
          then: (schema) =>
            schema.required("Campo requerido").matches(RFC_PM, "Formato incorrecto, inténtalo de nuevo"),
        }),
        // Tu negocio: solo PFAE.
        years_of_operation: isPFAERequest
          ? yup.string().required("Campo requerido").test("entero", "Escribe un número entero", isNonNegativeInteger)
          : yup.string(),
        number_of_employees: yup.string().test("entero", "Escribe un número entero", isNonNegativeInteger),
        // Solicitante
        name: yup.string().required("Campo requerido").matches(new RegExp(NAME), "Nombre inválido, revisa el formato"),
        first_lastname: yup
          .string()
          .required("Campo requerido")
          .matches(new RegExp(LAST_NAMES), "Apellido paterno inválido, revisa el formato"),
        second_lastname: yup.string().matches(new RegExp(LAST_NAMES), "Apellido materno inválido, revisa el formato"),
        country_code: yup.string().required("Campo requerido"),
        phone: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
        person_type_id: yup.string().required("Campo requerido"),
        rol_detail: yup.string().when("person_type_id", {
          is: otherPersonTypeId,
          then: (schema) => schema.required("Campo requerido"),
        }),
      }),
    [otherPersonTypeId, RFC_REGEX, isPMRequest, isPFAERequest],
  );

  /** PM: grupo empresarial; PFAE: datos del negocio. Ambos en `business_group` + `business_group_detail`. */
  const getBusinessPayload = (values: FormValues) => {
    if (isPMRequest) return toBusinessGroupPayload(values);
    if (isPFAERequest) return toPfaeBusinessPayload(values.years_of_operation, values.number_of_employees);
    return {};
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      rfc: companyData.rfc || "",
      company_name: companyData.name || "",
      enGrupoEmpresarial: "",
      numEmpresasGrupo: "",
      esMatrizGrupo: "",
      rfcMatrizGrupo: "",
      years_of_operation: "",
      number_of_employees: "",
      name: clientDetails.name || "",
      first_lastname: clientDetails.first_lastname || "",
      second_lastname: clientDetails.second_lastname || "",
      person_type_id: clientDetails.person_type_id || "",
      rol_detail: clientDetails.rol_detail || "",
      country_code: accountUser.code_phone || "+52",
      phone: accountUser.phone || "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      if (!formik.isValid) return;

      const rfc = values.rfc.trim();
      const companyName = values.company_name.trim();

      try {
        setSubmitting(true);

        // Si ya existe un borrador (el usuario vuelve a este paso), actualizamos en vez de dar de alta.
        const isExistingDraft = clientDetails.id !== EMPTY_ID;

        if (isExistingDraft) {
          const payloadAccountPatch: PayloadAccountPatch = {
            name: values.name,
            last_name: values.first_lastname,
            last_name_2: values.second_lastname,
            person_type_id: values.person_type_id,
            ...(rfcBloqueado ? {} : { company_rfc: rfc }),
            company_name: companyName,
            ...getBusinessPayload(values),
          };
          await patchAccount({ accountId: clientDetails.id, body: payloadAccountPatch }).unwrap();

          const payloadPatchUser: PayloadPatchUser = {
            rol_detail: values.rol_detail,
            rol_id: values.person_type_id,
            phone: `${values.country_code}${values.phone}`,
          };
          await patchUser({ id: String(accountUser.id), data: payloadPatchUser }).unwrap();

          await createPersonOrUpdatePerson(application.id, values);

          dispatch(
            setPersonalData({
              id: clientDetails.id,
              name: values.name,
              first_lastname: values.first_lastname,
              second_lastname: values.second_lastname,
              person_type_id: values.person_type_id,
              rol_detail: values.rol_detail,
              cell_phone_verified: clientDetails.cell_phone_verified,
              country_code: "",
              phone: "",
            }),
          );
          dispatch(
            setCompanyData({
              id: clientDetails.id,
              rfc,
              name: companyName,
              legal_name: "",
              role: "",
              ciec_pass: "",
              authorize_sat: false,
              is_correct_ciec: companyData.is_correct_ciec,
            }),
          );
          dispatch(
            setAccountUserPhone({
              id: accountUser.id,
              phone_full: `${values.country_code}${values.phone}`,
              code_phone: values.country_code,
              phone: values.phone,
            }),
          );
        } else {
          const payloadRegisterUser: PayloadRegisterUser = {
            username: accountUser.email,
            email: accountUser.email,
            password: accountUser.password,
            phone: `${values.country_code}${values.phone}`,
            rol_detail: values.rol_detail,
            rol_id: values.person_type_id,
            loan_application: {
              product_id: application.product_id as UUID,
              tax_systems_id: String(application.tax_system_id),
              source_code: application.source_code,
              financed_services_id: application.financed_services_id,
              guarantee_type_id: application.guarantee_type_id,
              description_guarantee: application.description_guarantee,
              guarantee_types: application.guarantee_types?.length ? application.guarantee_types : null,
              convenio_id: application.convenio_id ?? null,
              payroll_agreement_id: application.convenio_id ?? null,
              leasing_type_id: application.leasing_type_id ?? null,
              quoter_details: application.quoter_details ?? null,
            },
            application_condition: {
              requested_amount: application.loan_amount,
              requested_term: application.loan_term,
              selected_amount: application.loan_amount,
              selected_term: application.loan_term,
              approved_max_term: taxSystems?.maxTerm,
              approved_min_term: taxSystems?.minTerm,
              loan_amount: application.loan_amount,
              approved_amount: taxSystems?.maxAmount,
              selected_advance_percentage: application.advance_percentage || null,
              selected_residual_percentage: application.residual_percentage || null,
              requested_advance_percentage: application.advance_percentage || null,
              requested_residual_percentage: application.residual_percentage || null,
            },
            client_details: {
              name: values.name,
              last_name: values.first_lastname,
              last_name_2: values.second_lastname,
              person_type_id: values.person_type_id,
              tax_systems_id: String(application.tax_system_id),
              is_previous_client: clientDetails.is_previous_client,
              company_rfc: rfc,
              company_name: companyName,
              country_code: "",
              cell_phone_number: "",
            },
          };

          const responseRegisterUser = await registerUser(payloadRegisterUser).unwrap();

          await signIn({ username: accountUser.email, password: accountUser.password });
          await getSessionInfo(responseRegisterUser.data.sso_id).unwrap();

          // El registro no recibe el grupo empresarial ni los datos del negocio: se guardan en la cuenta
          // recién creada.
          await patchAccount({
            accountId: responseRegisterUser.data.client_details.id,
            body: getBusinessPayload(values),
          }).unwrap();

          dispatch(
            setAccountUserPhone({
              id: responseRegisterUser.data.id,
              phone_full: `${values.country_code}${values.phone}`,
              code_phone: values.country_code,
              phone: values.phone,
            }),
          );
          dispatch(setLoanId(responseRegisterUser.data.loan_application.id));
          dispatch(setClientDetailsId(responseRegisterUser.data.client_details.id));
          dispatch(setId_application(Number(responseRegisterUser.data.loan_application.id_application)));
          dispatch(set_Id_applicationCondition(String(responseRegisterUser.data.application_condition.id)));
          dispatch(
            setPersonalData({
              id: responseRegisterUser.data.client_details.id,
              name: values.name,
              first_lastname: values.first_lastname,
              second_lastname: values.second_lastname,
              person_type_id: values.person_type_id,
              rol_detail: values.rol_detail,
              cell_phone_verified: false,
              country_code: "",
              phone: "",
            }),
          );
          dispatch(
            setCompanyData({
              id: responseRegisterUser.data.client_details.id,
              rfc,
              name: companyName,
              legal_name: "",
              role: "",
              ciec_pass: "",
              authorize_sat: false,
              is_correct_ciec: false,
            }),
          );

          await createPersonOrUpdatePerson(responseRegisterUser.data.loan_application.id, values);
        }

        if (!clientDetails.cell_phone_verified) {
          const payloadRequestPhoneCode: PayloadRequestPhoneCode = {
            country_code: values.country_code,
            phone_number: values.phone,
          };
          if (clientDetails.phone === "") {
            await requestPhoneCode(payloadRequestPhoneCode).unwrap();
          } else if (clientDetails.phone !== values.phone) {
            await requestPhoneCode(payloadRequestPhoneCode).unwrap();
          }
        }

        const { code } = STATUS_CATALOG.validacion_telefono;

        dispatch(setLoanStatus(code));
        navigate(PATHS_TOFU_V2.validacionTelefono.path);
      } catch (error) {
        const api_error = error as ApiError;

        if (api_error.status && api_error.data) {
          if (api_error.data.code === "002") {
            formik.setFieldError("phone", "El teléfono ya se encuentra registrado, intenta con otro número");
            return;
          }
          if (api_error.data.code === "001") {
            formik.setFieldError("name", "El correo ya se encuentra registrado, intenta con otro correo");
            return;
          }
          // Mismo código y mensaje que el legacy (`tofu/pm/RegistroDatosPersonales`).
          if (api_error.data.code === "000") {
            formik.setFieldError("rfc", `Existe una cuenta con RFC - ${values.rfc}`);
            return;
          }
          if (api_error?.status === 429) {
            formik.setFieldError(
              "phone",
              "Excedió el número de intentos permitidos para ese número, intenta más tarde o contacta a un asesor.",
            );
            return;
          }
        }

        Sentry.captureException(error, { extra: { component: "RegistroDatosPersonales", values: formik.values } });
        dispatch(setModalErrorVisible({ open: true, error: api_error }));
      } finally {
        setSubmitting(false);
      }
    },
    validateOnMount: true,
  });

  const { values, setFieldValue, setValues, validateForm } = formik;

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoadingInitial(true);

        const result = await getPersonTypesTrigger().unwrap();
        const taxSystemConfig = await getTaxSystemConfig(application.type).unwrap();
        setTaxSystems(taxSystemConfig);

        const filteredData = result
          .filter(
            (item) =>
              (item.tax_system_type === application.tax_system_type || item.tax_systems_id === null) &&
              item.use_for_rol,
          )
          .sort((a, b) => a.name.localeCompare(b.name));
        setPersonTypeCatalog(filteredData);

        const otherItem = filteredData.find((item) => item.name.toLowerCase() === "otro");
        if (otherItem) setOtherPersonTypeId(otherItem.id);

        if (accountUser.id !== null && accountUser.id !== 0 && accountUser.id !== undefined) {
          const userResponse = await getUserData({ id: String(accountUser.id) }).unwrap();
          formik.setFieldValue("rol_detail", userResponse.rol_detail || "");
        }

        const accionistaMayoritario = result.find((item) => item.code === PERSON_CATALOG.accionista_mayoritario.code);
        const representanteLegal = result.find((item) => item.code === PERSON_CATALOG.representante_legal.code);
        const ids: string[] = [];
        if (accionistaMayoritario?.id) ids.push(String(accionistaMayoritario.id));
        if (representanteLegal?.id) ids.push(String(representanteLegal.id));
        setPersonTypeIdsForRep(ids);
      } catch (error) {
        console.error("Ha ocurrido un error al consultar el catalogo", error);
      }

      // Al volver a este paso: grupo empresarial (PM) o datos del negocio (PFAE) guardados en la cuenta.
      // Va aparte para que una falla aquí no deje sin catálogo de roles (carga con respaldo: solo Sentry).
      if (clientDetails.id !== EMPTY_ID) {
        await prefillBusinessData();
      }
      setIsLoadingInitial(false);
    };

    /**
     * `setValues(..., true)` valida con los valores que recibe. Un `validateForm()` aparte corría con los
     * valores de antes de precargar (formik aún no se re-renderizaba) y dejaba "Continuar" deshabilitado.
     */
    const prefillBusinessData = async () => {
      try {
        const account = await getAccount({ id: clientDetails.id }).unwrap();
        const detail = account?.data?.business_group_detail;
        if (isPMRequest) {
          const grupo = fromBusinessGroupResponse(account?.data?.business_group, detail);
          if (grupo) {
            await formik.setValues((prev) => ({ ...prev, ...grupo }), true);
          }
        } else if (isPFAERequest) {
          const negocio = fromPfaeBusinessResponse(detail);
          await formik.setValues(
            (prev) => ({
              ...prev,
              years_of_operation: negocio.yearsOfOperation,
              number_of_employees: negocio.employees,
            }),
            true,
          );
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "RegistroDatosPersonales-account" } });
      }
    };

    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const createPersonOrUpdatePerson = async (loanApplicationId: string, formValues: FormValues) => {
    if (!isPFAERequest) return;

    const payLegalRepresentativeData: PayloadPersonPatch = {
      person_types: personTypeIdsForRep,
      name: formValues.name,
      last_name: formValues.first_lastname,
      last_name_2: formValues.second_lastname,
      loan_application_id: loanApplicationId,
      code_phone_number: formValues.country_code,
      phone_number: formValues.phone,
      email: accountUser.email,
      rfc: formValues.rfc.trim() || undefined,
      is_shareholder: true,
    };

    if (personId) {
      await patchPerson({ personId, body: payLegalRepresentativeData }).unwrap();
    } else {
      const responseCreate = await saveLegalRepresentative({ body: payLegalRepresentativeData }).unwrap();
      setPersonId(responseCreate.data.id);
      dispatch(setLegalRepresentative(responseCreate.data));
    }
  };

  /** "No" en grupo empresarial limpia el detalle del grupo. */
  const handleChangeGrupoEmpresarial = (value: string | number) => {
    if (value === "No") {
      setValues({ ...values, enGrupoEmpresarial: "No", numEmpresasGrupo: "", esMatrizGrupo: "", rfcMatrizGrupo: "" });
    } else {
      setFieldValue("enGrupoEmpresarial", value);
    }
    setTimeout(() => validateForm(), 200);
  };

  /** Si la empresa es la matriz no aplica el RFC de la matriz, así que se limpia. */
  const handleChangeEsMatrizGrupo = (value: string | number) => {
    if (value === "Sí") {
      setValues({ ...values, esMatrizGrupo: "Sí", rfcMatrizGrupo: "" });
    } else {
      setFieldValue("esMatrizGrupo", value);
    }
    setTimeout(() => validateForm(), 200);
  };

  const bloqueado = formik.isSubmitting || isLoadingInitial;
  const rfcValido = !formik.errors.rfc && values.rfc.length === RFC_MAX_LENGTH;

  return (
    <Box>
      <PageHeader
        eyebrow="Datos del usuario"
        title="Cuéntanos un poco de ti"
        subtitle="Datos básicos para crear tu solicitud. Con el RFC identificamos si solicitas como empresa o como persona física con actividad empresarial."
      />

      <Paper variant="lendia_paper" sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <SectionHeader label={isPFAERequest ? "Tu negocio" : "Empresa"} first />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 14px" }}>
          <FieldWrapper
            label="RFC"
            full
            hint={
              rfcValido
                ? undefined
                : "12 caracteres si eres persona moral, 13 si eres persona física con actividad empresarial."
            }
            error={(formik.touched.rfc && formik.errors.rfc) || ""}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              value={values.rfc}
              onChange={(e) => setFieldValue("rfc", e.target.value.toUpperCase().replace(/[^A-ZÑ&0-9]/g, ""))}
              onBlur={formik.handleBlur}
              name="rfc"
              placeholder={isPMRequest ? "ABC123456XYZ" : "ABCD800101XY1"}
              inputProps={{
                maxLength: RFC_MAX_LENGTH,
                style: { textTransform: "uppercase", fontVariantNumeric: "tabular-nums" },
                "data-testid": "registro-datos-rfc-input",
              }}
              disabled={bloqueado || rfcBloqueado}
            />
            {/* Pastilla del diseño: confirma el tipo de persona cuando el RFC ya es válido. */}
            {rfcValido && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1.25,
                  px: "13px",
                  py: "7px",
                  borderRadius: 99,
                  color: "primary.main",
                  bgcolor: (theme) => `color-mix(in oklab, ${theme.palette.primary.main} 10%, transparent)`,
                  border: (theme) => `1px solid color-mix(in oklab, ${theme.palette.primary.main} 32%, transparent)`,
                }}
              >
                <Check sx={{ fontSize: 14 }} />
                <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: "inherit" }}>
                  {isPMRequest ? "Persona moral" : "Persona física con actividad empresarial (PFAE)"}
                </Typography>
              </Box>
            )}
          </FieldWrapper>
          <FieldWrapper
            label="Nombre comercial"
            full
            hint={isPFAERequest ? "El nombre con el que te conocen tus clientes, si es distinto al tuyo." : undefined}
            error={(formik.touched.company_name && formik.errors.company_name) || ""}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              value={values.company_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="company_name"
              placeholder="Refacciones Vega"
              inputProps={{ maxLength: 150, "data-testid": "registro-datos-nombre-comercial-input" }}
              disabled={bloqueado}
            />
          </FieldWrapper>
          {isPFAERequest && (
            <>
              <FieldWrapper
                label="Años de operación"
                error={(formik.touched.years_of_operation && formik.errors.years_of_operation) || ""}
              >
                <TextField
                  autoComplete="off"
                  size="lendia_input"
                  fullWidth
                  value={values.years_of_operation}
                  onChange={(e) =>
                    setFieldValue("years_of_operation", e.target.value.replace(/[^0-9]/g, "").slice(0, 3))
                  }
                  onBlur={formik.handleBlur}
                  name="years_of_operation"
                  placeholder="ej. 6"
                  inputProps={{ inputMode: "numeric", "data-testid": "registro-datos-anios-operacion-input" }}
                  disabled={bloqueado}
                />
              </FieldWrapper>
              <FieldWrapper
                label="Empleados"
                optionalSuffix="(opcional)"
                error={(formik.touched.number_of_employees && formik.errors.number_of_employees) || ""}
              >
                <TextField
                  autoComplete="off"
                  size="lendia_input"
                  fullWidth
                  value={values.number_of_employees}
                  onChange={(e) =>
                    setFieldValue("number_of_employees", e.target.value.replace(/[^0-9]/g, "").slice(0, 6))
                  }
                  onBlur={formik.handleBlur}
                  name="number_of_employees"
                  placeholder="ej. 4"
                  inputProps={{ inputMode: "numeric", "data-testid": "registro-datos-empleados-input" }}
                  disabled={bloqueado}
                />
              </FieldWrapper>
            </>
          )}
        </Box>

        {isPMRequest && (
          <>
            <SectionHeader label="Grupo empresarial" />
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px 14px" }}>
              <FieldWrapper
                label="¿Tu empresa pertenece a un grupo empresarial?"
                full
                error={(formik.submitCount > 0 && formik.errors.enGrupoEmpresarial) || ""}
              >
                <ChoiceToggle
                  value={values.enGrupoEmpresarial}
                  onChange={handleChangeGrupoEmpresarial}
                  options={SI_NO_OPTIONS}
                  disabled={bloqueado}
                />
              </FieldWrapper>

              {values.enGrupoEmpresarial === "Sí" && (
                <>
                  <FieldWrapper
                    label="Número de empresas en el grupo"
                    error={(formik.touched.numEmpresasGrupo && formik.errors.numEmpresasGrupo) || ""}
                  >
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      type="number"
                      inputProps={{ min: 2, "data-testid": "registro-datos-num-empresas-input" }}
                      value={values.numEmpresasGrupo}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      name="numEmpresasGrupo"
                      placeholder="ej. 3"
                      disabled={bloqueado}
                    />
                  </FieldWrapper>

                  <FieldWrapper
                    label="¿Tu empresa es la matriz del grupo?"
                    full
                    error={(formik.submitCount > 0 && formik.errors.esMatrizGrupo) || ""}
                  >
                    <ChoiceToggle
                      value={values.esMatrizGrupo}
                      onChange={handleChangeEsMatrizGrupo}
                      options={SI_NO_OPTIONS}
                      disabled={bloqueado}
                    />
                  </FieldWrapper>

                  {values.esMatrizGrupo === "No" && (
                    <FieldWrapper
                      label="RFC de la empresa matriz"
                      full
                      error={(formik.touched.rfcMatrizGrupo && formik.errors.rfcMatrizGrupo) || ""}
                    >
                      <TextField
                        autoComplete="off"
                        size="lendia_input"
                        fullWidth
                        value={values.rfcMatrizGrupo}
                        onChange={(e) => setFieldValue("rfcMatrizGrupo", e.target.value.toUpperCase())}
                        onBlur={formik.handleBlur}
                        name="rfcMatrizGrupo"
                        placeholder="XYZ123456ABC"
                        inputProps={{
                          maxLength: 12,
                          style: { textTransform: "uppercase" },
                          "data-testid": "registro-datos-rfc-matriz-input",
                        }}
                        disabled={bloqueado}
                      />
                    </FieldWrapper>
                  )}
                </>
              )}
            </Box>
          </>
        )}

        <SectionHeader label={isPFAERequest ? "Tus datos" : "Solicitante"} />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" }, gap: "12px 14px" }}>
          <FieldWrapper label="Nombre(s)" error={(formik.touched.name && formik.errors.name) || ""}>
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              value={values.name}
              onChange={(e) => setFieldValue("name", transformTitleCase(e.target.value))}
              onBlur={formik.handleBlur}
              name="name"
              placeholder="Carla"
              inputProps={{ maxLength: 100, "data-testid": "registro-datos-nombre-input" }}
              disabled={bloqueado}
            />
          </FieldWrapper>
          <FieldWrapper
            label="Apellido paterno"
            error={(formik.touched.first_lastname && formik.errors.first_lastname) || ""}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              value={values.first_lastname}
              onChange={(e) => setFieldValue("first_lastname", transformTitleCase(e.target.value))}
              onBlur={formik.handleBlur}
              name="first_lastname"
              placeholder="Robles"
              inputProps={{ maxLength: 100, "data-testid": "registro-datos-apellido-paterno-input" }}
              disabled={bloqueado}
            />
          </FieldWrapper>
          <FieldWrapper
            label="Apellido materno"
            optionalSuffix="(opcional)"
            error={(formik.touched.second_lastname && formik.errors.second_lastname) || ""}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              fullWidth
              value={values.second_lastname}
              onChange={(e) => setFieldValue("second_lastname", transformTitleCase(e.target.value))}
              onBlur={formik.handleBlur}
              name="second_lastname"
              placeholder="Mendoza"
              inputProps={{ maxLength: 100, "data-testid": "registro-datos-apellido-materno-input" }}
              disabled={bloqueado}
            />
          </FieldWrapper>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          <FieldWrapper
            label="Rol en la empresa"
            full
            error={(formik.touched.person_type_id && formik.errors.person_type_id) || ""}
          >
            <TextField
              autoComplete="off"
              size="lendia_input"
              select
              fullWidth
              value={values.person_type_id}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="person_type_id"
              inputProps={{ "data-testid": "registro-datos-rol-select" }}
              disabled={bloqueado}
            >
              <MenuItem value="">Selecciona…</MenuItem>
              {personTypeCatalog.length > 0 ? (
                personTypeCatalog.map((item) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No hay opciones disponibles
                </MenuItem>
              )}
            </TextField>
          </FieldWrapper>
          {values.person_type_id === otherPersonTypeId && (
            <FieldWrapper
              label="Especifica otro"
              full
              error={(formik.touched.rol_detail && formik.errors.rol_detail) || ""}
            >
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                value={values.rol_detail}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                name="rol_detail"
                inputProps={{ maxLength: 150, "data-testid": "registro-datos-rol-detalle-input" }}
                disabled={bloqueado}
              />
            </FieldWrapper>
          )}
          <FieldWrapper label="Teléfono celular" full error={(formik.touched.phone && formik.errors.phone) || ""}>
            <Box sx={{ display: "flex", gap: 1 }}>
              <CountrySelect
                value={values.country_code}
                onChange={(v) => setFieldValue("country_code", v)}
                disabled={bloqueado || clientDetails.cell_phone_verified}
              />
              <TextField
                autoComplete="off"
                size="lendia_input"
                fullWidth
                value={values.phone}
                onChange={(e) => setFieldValue("phone", e.target.value.replace(/[^0-9]/g, "").slice(0, 10))}
                onBlur={formik.handleBlur}
                name="phone"
                placeholder="55 1234 5678"
                inputProps={{ "data-testid": "registro-datos-telefono-input" }}
                disabled={bloqueado || clientDetails.cell_phone_verified}
              />
            </Box>
          </FieldWrapper>
        </Box>
      </Paper>

      <StepActions
        onBack={handleBack}
        onNext={formik.submitForm}
        nextDisabled={!formik.isValid || bloqueado}
        backTestId="registro-datos-back-button"
        nextTestId="registro-datos-continue-button"
      />
    </Box>
  );
};
